(function () {
	if (window.customElements.get('usage-video-list')) return;

	var HOLD_DELAY = 200;
	var TAP_MOVE_LIMIT = 10;
	var SWIPE_DOWN_DISTANCE = 80;
	var BLEED_QUERY = '(max-width: 999px)';
	// The click that follows the tap which closed the modal lands on the card underneath; ignore it
	var GHOST_CLICK_WINDOW = 500;

	class UsageVideoList extends HTMLElement {
		connectedCallback() {
			this._cards = Array.from(this.querySelectorAll('[data-usage-video-card]'));
			this._modal = this.querySelector('[data-usage-video-modal]');
			if (!this._cards.length || !this._modal) return;

			this._row = this.querySelector('.usage-video-list__row');
			this._player = this._modal.querySelector('[data-usage-video-player]');
			this._tap = this._modal.querySelector('[data-usage-video-tap]');
			this._stage = this._modal.querySelector('[data-usage-video-stage]');
			this._progress = this._modal.querySelector('[data-usage-video-progress]');
			this._voice = this._modal.querySelector('[data-usage-video-modal-voice]');
			this._soundButton = this._modal.querySelector('[data-usage-video-sound]');
			this._closeButton = this._modal.querySelector('[data-usage-video-close]');

			this._index = 0;
			this._isOpen = false;
			this._closedAt = 0;
			this._held = false;
			this._pointer = null;
			this._raf = 0;

			this._onKeydown = this._onKeydown.bind(this);
			this._tick = this._tick.bind(this);

			this._cards.forEach((card, index) => {
				card.addEventListener('click', () => {
					if (Date.now() - this._closedAt < GHOST_CLICK_WINDOW) return;
					this.open(index);
				});
			});

			this._soundButton.addEventListener('click', () => this._setMuted(!this._player.muted));
			this._closeButton.addEventListener('click', () => this.close());
			this._player.addEventListener('ended', () => this._go(this._index + 1));

			this._tap.addEventListener('pointerdown', (event) => this._onPointerDown(event));
			this._tap.addEventListener('pointermove', (event) => this._onPointerMove(event));
			this._tap.addEventListener('pointerup', (event) => this._onPointerUp(event));
			this._tap.addEventListener('pointercancel', () => this._onPointerCancel());

			this._initSlider();
		}

		_initSlider() {
			// Flickity is loaded globally in theme.liquid; without it the row stays a native scroll-snap row
			if (!this._row || typeof window.Flickity !== 'function') return;

			this._onResize = () => {
				cancelAnimationFrame(this._resizeRaf);
				this._resizeRaf = requestAnimationFrame(() => this._updateBleed());
			};

			this._updateBleed();
			this._flickity = new window.Flickity(this._row, {
				cellAlign: 'left',
				contain: true,
				prevNextButtons: false,
				pageDots: false,
				wrapAround: false,
				freeScroll: false,
				adaptiveHeight: false,
				imagesLoaded: true
			});
			window.addEventListener('resize', this._onResize);
		}

		// On the single-column PDP the row runs to the screen edges; first/last card get the content offset as margin
		_updateBleed() {
			if (window.matchMedia(BLEED_QUERY).matches) {
				this._row.style.setProperty('--bleed', Math.max(this.getBoundingClientRect().left, 0) + 'px');
			} else {
				this._row.style.removeProperty('--bleed');
			}
			if (this._flickity) this._flickity.resize();
		}

		disconnectedCallback() {
			if (this._onResize) window.removeEventListener('resize', this._onResize);
			cancelAnimationFrame(this._resizeRaf);
			if (this._flickity) {
				this._flickity.destroy();
				this._flickity = null;
			}
			if (this._isOpen) this.close({ restoreFocus: false });
			// The modal is moved to <body> while open, make sure it never outlives the component
			if (this._modal && this._modal.parentNode === document.body) this._modal.remove();
		}

		open(index) {
			if (this._isOpen) return;
			this._isOpen = true;
			this._opener = this._cards[index];

			// Render outside of any transformed/overflow-clipped ancestor of the PDP
			this._modal.style.setProperty('--usage-video-star', this.style.getPropertyValue('--usage-video-star'));
			document.body.appendChild(this._modal);

			this._buildProgress();
			this._setMuted(true);
			this._modal.hidden = false;
			document.documentElement.classList.add('usage-video-open');
			document.addEventListener('keydown', this._onKeydown);

			this._go(index);
			this._closeButton.focus({ preventScroll: true });
		}

		close(options) {
			if (!this._isOpen) return;
			this._isOpen = false;
			this._closedAt = Date.now();
			cancelAnimationFrame(this._raf);
			clearTimeout(this._holdTimer);

			this._player.pause();
			this._player.removeAttribute('src');
			this._player.removeAttribute('poster');
			this._player.load();

			this._modal.hidden = true;
			this._modal.style.transform = '';
			this._modal.style.opacity = '';
			this.appendChild(this._modal);

			// Closing never touches window scroll, so the PDP stays at the same position
			document.documentElement.classList.remove('usage-video-open');
			document.removeEventListener('keydown', this._onKeydown);

			if (!options || options.restoreFocus !== false) {
				if (this._opener) this._opener.focus({ preventScroll: true });
			}
		}

		_buildProgress() {
			this._progress.innerHTML = '';
			this._segments = this._cards.map(() => {
				var segment = document.createElement('span');
				segment.className = 'usage-video-modal__segment';
				var fill = document.createElement('span');
				fill.className = 'usage-video-modal__segment-fill';
				segment.appendChild(fill);
				this._progress.appendChild(segment);
				return fill;
			});
		}

		_go(index) {
			// After the last video the modal closes
			if (index >= this._cards.length) {
				this.close();
				return;
			}
			// Previous on the first video restarts it
			this._index = Math.max(index, 0);

			var card = this._cards[this._index];
			var poster = card.getAttribute('data-video-poster');

			if (poster) this._player.setAttribute('poster', poster);
			else this._player.removeAttribute('poster');
			this._player.src = card.getAttribute('data-video-src');
			this._player.currentTime = 0;
			this._play();

			this._segments.forEach((fill, i) => {
				fill.style.width = i < this._index ? '100%' : '0%';
			});

			// Customer voice chip comes from the card of the playing video
			var voice = card.querySelector('[data-usage-video-voice]');
			this._voice.innerHTML = voice ? voice.outerHTML : '';

			cancelAnimationFrame(this._raf);
			this._raf = requestAnimationFrame(this._tick);
		}

		_play() {
			var result = this._player.play();
			if (result && typeof result.catch === 'function') result.catch(function () {});
		}

		_tick() {
			if (!this._isOpen) return;
			var duration = this._player.duration;
			if (duration && isFinite(duration)) {
				var fraction = Math.min(this._player.currentTime / duration, 1);
				this._segments[this._index].style.width = fraction * 100 + '%';
			}
			this._raf = requestAnimationFrame(this._tick);
		}

		_setMuted(muted) {
			this._player.muted = muted;
			var button = this._soundButton;
			button.querySelector('img').src = button.getAttribute(muted ? 'data-icon-off' : 'data-icon-on');
			button.setAttribute('aria-label', button.getAttribute(muted ? 'data-label-on' : 'data-label-off'));
		}

		_onKeydown(event) {
			if (event.key === 'Escape') this.close();
			else if (event.key === 'ArrowRight') this._go(this._index + 1);
			else if (event.key === 'ArrowLeft') this._go(this._index - 1);
		}

		_onPointerDown(event) {
			this._pointer = { x: event.clientX, y: event.clientY, dx: 0, dy: 0 };
			this._held = false;
			this._tap.setPointerCapture(event.pointerId);

			// Press and hold: pause, release: resume
			this._holdTimer = setTimeout(() => {
				this._held = true;
				this._player.pause();
			}, HOLD_DELAY);
		}

		_onPointerMove(event) {
			if (!this._pointer) return;
			this._pointer.dx = event.clientX - this._pointer.x;
			this._pointer.dy = event.clientY - this._pointer.y;

			var moved = Math.max(Math.abs(this._pointer.dx), Math.abs(this._pointer.dy));
			if (moved > TAP_MOVE_LIMIT) clearTimeout(this._holdTimer);

			// Swipe down feedback
			if (this._pointer.dy > TAP_MOVE_LIMIT && this._pointer.dy > Math.abs(this._pointer.dx)) {
				this._modal.style.transform = 'translateY(' + this._pointer.dy + 'px)';
				this._modal.style.opacity = String(Math.max(1 - this._pointer.dy / 400, 0.4));
			}
		}

		_onPointerUp(event) {
			if (!this._pointer) return;
			clearTimeout(this._holdTimer);

			var pointer = this._pointer;
			this._pointer = null;
			this._modal.style.transform = '';
			this._modal.style.opacity = '';

			if (pointer.dy > SWIPE_DOWN_DISTANCE && pointer.dy > Math.abs(pointer.dx)) {
				this.close();
				return;
			}

			if (this._held) {
				this._held = false;
				this._play();
				return;
			}

			var isTap = Math.max(Math.abs(pointer.dx), Math.abs(pointer.dy)) <= TAP_MOVE_LIMIT;
			if (!isTap) return;

			// Tap right half: next video, tap left half: previous video
			var rect = this._stage.getBoundingClientRect();
			var isRightHalf = event.clientX - rect.left > rect.width / 2;
			this._go(this._index + (isRightHalf ? 1 : -1));
		}

		_onPointerCancel() {
			clearTimeout(this._holdTimer);
			this._pointer = null;
			this._modal.style.transform = '';
			this._modal.style.opacity = '';
			if (this._held) {
				this._held = false;
				this._play();
			}
		}
	}

	window.customElements.define('usage-video-list', UsageVideoList);
})();
